import { useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { SessionBanner } from './SessionBanner';
import { Sidebar } from './Sidebar';
import type { SidebarUser } from './Sidebar';
import styles from './AppLayout.module.css';

export interface AppLayoutProps {
  readonly user: SidebarUser;
  readonly isAdmin: boolean;
}

/**
 * Application shell. RequireAuth supplies the authenticated user.
 *
 * The skip link is an addition — the prototype had none, so keyboard users had to tab
 * through ten navigation items on every page to reach the content.
 */
export function AppLayout({ user, isAdmin }: AppLayoutProps) {
  const { pathname } = useLocation();
  const viewportRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (viewportRef.current) {
      viewportRef.current.scrollTop = 0;
    }
    if (typeof window.scrollTo === 'function') {
      window.scrollTo(0, 0);
    }
  }, [pathname]);

  return (
    <div className={styles.container}>
      <a href="#main-content" className={styles.skipLink}>
        Skip to content
      </a>
      <Sidebar user={user} isAdmin={isAdmin} />
      <div className={styles.viewport} ref={viewportRef}>
        <SessionBanner />
        <main id="main-content" className={styles.content} tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
